import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BaseService} from '../services/base.service';
import {Observable} from 'rxjs';
import {LabSubmissionModel} from './models/lab-submission';
import { VitaReportResponse } from './recommendation-details/recommendation-details.component';

@Injectable({
  providedIn: 'root'
})
export class RecommendationManagerService extends BaseService {

  private API_RECOMMENDATION_MANAGER_URL = this.API_ADMIN_BASE_URL + "v1/recommendation-manager";

  constructor(
      private http: HttpClient,
      private snackBar: MatSnackBar) {
    super()
  }

  setParameter(currentPage: number, pageSize: number, fromDate: string, toDate: string, searchText: string): HttpParams {
    let params = new HttpParams()
        .set('page', currentPage.toString())
        .append('size', pageSize.toString());

    if (fromDate) {
      params = params.append('fromDate', fromDate);
    }

    if (toDate) {
      params = params.append('toDate', toDate);
    }

    if (searchText) {
      params = params.append('searchQuery', searchText);
    }

    return params;
  }

  saveRecommendationDraft(recommendationId: string, recommendationNote: string, comments: string) {
    let body = {
      recommendationNote: recommendationNote,
      comments: comments
    }
    return this.http.put<any>(this.API_RECOMMENDATION_MANAGER_URL + `/recommendations/${recommendationId}`, body);
  }

  deleteRecommendation(recommendationId: string) {
    return this.http.delete<any>(this.API_RECOMMENDATION_MANAGER_URL + `/recommendations/${recommendationId}`);
  }

  createInterpretation(normData: any) {
    return this.http.post<any>(this.API_RECOMMENDATION_MANAGER_URL + `/soft-tissue/`, normData);
  }

  getRecManTypes(labTypeId: number) {
    let params = new HttpParams();

    if(labTypeId > 0) {
      params.set('labTypeId', labTypeId.toString());
    }

    return this.http.get<any[]>(this.API_RECOMMENDATION_MANAGER_URL + `/meta/rec-man-types`, {params});
  }

  getSoilSubmissions(customerId: number, typesId: number) {
    console.log('We are here')
    return this.http.get<any[]>(this.API_RECOMMENDATION_MANAGER_URL + `/meta/submissions?customerId=${customerId}&typesId=${typesId}`);
  }

  getSoilSamples(submissionId: number) {
    return this.http.get<any[]>(this.API_RECOMMENDATION_MANAGER_URL + `/api/soil-preparation/submissions/${submissionId}/samples`);
  }

  createSoilPreparation(dto: { labManSubmissionId: number; recManTypesId: number; pinSamplesIds: number[] }) {
    return this.http.post<any>(this.API_RECOMMENDATION_MANAGER_URL + `/api/soil-preparation`, dto);
  }

  getLabSubmissionList(clientId: number, typesId?: number): Observable<LabSubmissionModel[]> {
    let params = new HttpParams()
        .set('customerId', clientId.toString());
    if (typesId != null) params = params.set('typesId', typesId.toString());

    return this.http.get<LabSubmissionModel[]>(this.API_RECOMMENDATION_MANAGER_URL + '/meta/submissions', {params});
  }

  getRecommendationList(currentPage: number, pageSize: number, fromDate: string, toDate: string, searchText: string, clientId: number, recManTypeId: number): Observable<any> {
    let params = this.setParameter(currentPage, pageSize, fromDate, toDate, searchText);

    if(clientId && clientId > 0) {
      params = params.append('clientId', clientId);
    }

    if(recManTypeId && recManTypeId > 0) {
      params = params.append('typeId', recManTypeId);
    }

    return this.http.get<any>(this.API_RECOMMENDATION_MANAGER_URL + '/recommendations/main', {params});
  }

  downloadSoftTissueReport(recommendationId: string): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(this.API_RECOMMENDATION_MANAGER_URL + `/samples/${recommendationId}/report`, {
      observe: 'response',
      responseType: 'blob' as 'json'
    } );
  }

  downloadVitaReport(recommendationId: string, vitaReportResponse: VitaReportResponse[]): Observable<HttpResponse<Blob>> {
    return this.http.post<Blob>(this.API_RECOMMENDATION_MANAGER_URL + `/vita/${recommendationId}/report`, vitaReportResponse, {
      observe: 'response',
      responseType: 'blob' as 'json'
    } );
  }

  getSoftTissueList(currentPage: number, pageSize: number, fromDate: string, toDate: string, searchText: string, clientId: number): Observable<any> {
    let params = this.setParameter(currentPage, pageSize, fromDate, toDate, searchText);

    if (clientId && clientId > 0) {
      params = params.append('clientId', clientId);
    }

    return this.http.get<any>(this.API_RECOMMENDATION_MANAGER_URL + '/soft-tissue/list', {params});
  }

  getSoftTissueListAll(clientId: number, labSubmissionId: number): Observable<any> {
    let params = new HttpParams()
        .set('clientId', clientId.toString())
        .append('submissionId', labSubmissionId.toString())

    return this.http.get<any>(this.API_RECOMMENDATION_MANAGER_URL + '/soft-tissue/list-all', {params});
  }

  /**
   * Every crop definition a field has had, across all seasons, for linking a
   * sample that has none. Not filtered to a running season on purpose: the
   * samples needing a link are the ones whose season has ended.
   */
  getFieldDefinitions(fieldId: number): Observable<any[]> {
    return this.http.get<any[]>(this.API_RECOMMENDATION_MANAGER_URL + `/soft-tissue/field/${fieldId}/definitions`);
  }

  /**
   * The same, for every field in a lab submission at once, keyed by field id.
   *
   * One call per grid instead of one per field. The server resolves the fields
   * from the submission, so the caller does not need to know them.
   */
  getSubmissionDefinitions(submissionId: number): Observable<{ [fieldId: string]: any[] }> {
    return this.http.get<{ [fieldId: string]: any[] }>(
      this.API_RECOMMENDATION_MANAGER_URL + `/soft-tissue/submission/${submissionId}/definitions`);
  }

  getSoftTissueForRecommendation(recommendationId: string, searchText: string, page: number, pageSize: number) {
    let params = new HttpParams()
        .set('page', page.toString())
        .append('size', pageSize.toString());

    if (searchText) {
      params = params.append('searchQuery', searchText);
    }
    return this.http.get(this.API_RECOMMENDATION_MANAGER_URL + `/soft-tissue/${recommendationId}`, {params});
  }

  getRecommendationDetails(recommendationId: string) {
    return this.http.get(this.API_RECOMMENDATION_MANAGER_URL + `/recommendations/${recommendationId}`);
  }

  getCropMetaData(): Observable<any> {
    return this.http.get<any>(this.API_RECOMMENDATION_MANAGER_URL + '/meta/crop');
  }

  getGrowthStageMetaData(cropId: number, regionId: number): Observable<any> {
    let params = new HttpParams().set('cropId', cropId.toString())
        .append('regionId', regionId);
    return this.http.get<any>(this.API_RECOMMENDATION_MANAGER_URL + '/meta/growth-stage', {params});
  }

  getRegionsMetaData(cropId: number): Observable<any> {
    let params = new HttpParams().set('cropId', cropId.toString());
    return this.http.get<any>(this.API_RECOMMENDATION_MANAGER_URL + '/meta/regions', {params});
  }
}
